import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Hammer,
  CalendarRange,
  BookOpen,
  ArrowUpRight,
  Clock,
  MapPin,
  Layers,
  Link2,
} from "lucide-react";

const MODULES = [
  {
    key: "forge",
    to: "/forge",
    title: "App Forge",
    slug: "custom-app-forge-14",
    tagline: "Build custom apps from templates.",
    icon: Hammer,
    accentDot: "bg-indigo-500",
    accentBar: "bg-indigo-500",
    accentChip: "bg-indigo-50 text-indigo-700 border-indigo-200",
    testid: "module-card-forge",
  },
  {
    key: "calendar",
    to: "/calendar",
    title: "Calendar · Maps",
    slug: "calendar-maps-pod",
    tagline: "Events with place, pinned on a map.",
    icon: CalendarRange,
    accentDot: "bg-emerald-500",
    accentBar: "bg-emerald-500",
    accentChip: "bg-emerald-50 text-emerald-700 border-emerald-200",
    testid: "module-card-calendar",
  },
  {
    key: "quran",
    to: "/quran",
    title: "Quran Connections",
    slug: "quran-connections",
    tagline: "Thematic links between verses.",
    icon: BookOpen,
    accentDot: "bg-amber-500",
    accentBar: "bg-amber-500",
    accentChip: "bg-amber-50 text-amber-700 border-amber-200",
    testid: "module-card-quran",
  },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    forge_creations: 0,
    templates: 0,
    events: 0,
    upcoming: null,
    surahs: 0,
    verses_sample: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [tplRes, creRes, evRes, surRes] = await Promise.all([
          api.get("/forge/templates"),
          api.get("/forge/creations"),
          api.get("/calendar/events"),
          api.get("/quran/surahs"),
        ]);
        if (cancelled) return;

        // pick first surah's first verse for a little sample
        let sampleVerses = [];
        if (surRes.data?.[0]) {
          try {
            const firstSurah = surRes.data[0];
            const vRes = await api.get(`/quran/surahs/${firstSurah.number}/verses`);
            sampleVerses = vRes.data?.verses?.slice(0, 1) || [];
          } catch (e) {
            sampleVerses = [];
          }
        }

        const now = new Date();
        const upcoming = (evRes.data || [])
          .filter((e) => e.date && new Date(e.date) >= new Date(now.toISOString().slice(0, 10)))
          .sort((a, b) => a.date.localeCompare(b.date))[0];

        setStats({
          forge_creations: (creRes.data || []).length,
          templates: (tplRes.data || []).length,
          events: (evRes.data || []).length,
          upcoming: upcoming || null,
          surahs: (surRes.data || []).length,
          verses_sample: sampleVerses,
        });
      } catch (e) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Layout title="Overview" subtitle="FullEcosystem · dashboard">
      {/* Hero */}
      <section className="mb-10" data-testid="dashboard-hero">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-3">
          Welcome back
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900">
          Hello, {user?.name?.split(" ")[0] || "there"}.
        </h1>
        <p className="mt-4 text-base text-slate-600 max-w-2xl leading-relaxed">
          Three apps, one workspace. Jump between App Forge, Calendar · Maps,
          and Quran Connections — your session carries across all of them.
        </p>
      </section>

      {/* Module cards */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
        {MODULES.map((m) => {
          const Icon = m.icon;
          return (
            <Link
              key={m.key}
              to={m.to}
              data-testid={m.testid}
              className="group relative flex flex-col rounded-md border border-slate-200 bg-white p-6 hover:border-slate-300 hover:shadow-sm transition-all duration-150 overflow-hidden"
            >
              <div className={`absolute left-0 top-0 h-full w-1 ${m.accentBar}`} />
              <div className="flex items-start justify-between">
                <div className={`h-10 w-10 rounded-md border flex items-center justify-center ${m.accentChip}`}>
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-slate-900 transition-colors" />
              </div>
              <div className="mt-5">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1.5">
                  {m.slug}
                </div>
                <div className="font-display text-xl font-semibold tracking-tight text-slate-900">
                  {m.title}
                </div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {m.tagline}
                </p>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-200 flex items-center gap-2">
                <span className={`h-1.5 w-1.5 rounded-full ${m.accentDot}`} />
                <span className="font-mono text-[11px] text-slate-600" data-testid={`${m.testid}-stat`}>
                  {m.key === "forge" && `${stats.forge_creations} creations · ${stats.templates} templates`}
                  {m.key === "calendar" && `${stats.events} events scheduled`}
                  {m.key === "quran" && `${stats.surahs} surahs loaded`}
                </span>
              </div>
            </Link>
          );
        })}
      </section>

      {/* Quick widgets */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-md border border-slate-200 bg-white p-6" data-testid="widget-upcoming-event">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-emerald-700 mb-1">
                Calendar · Maps
              </div>
              <div className="font-display text-lg font-semibold text-slate-900">
                Next upcoming event
              </div>
            </div>
            <Link
              to="/calendar"
              className="text-xs font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="widget-upcoming-event-open"
            >
              Open <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {loading ? (
            <div className="mt-6 text-sm text-slate-500">Loading…</div>
          ) : stats.upcoming ? (
            <div className="mt-6 flex items-start gap-4">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-center min-w-[72px]">
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  {new Date(stats.upcoming.date).toLocaleDateString(undefined, { month: "short" })}
                </div>
                <div className="font-display text-2xl font-bold text-slate-900">
                  {new Date(stats.upcoming.date).getDate()}
                </div>
              </div>
              <div className="min-w-0">
                <div className="font-display text-lg font-semibold text-slate-900 truncate">
                  {stats.upcoming.title}
                </div>
                <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-600">
                  {stats.upcoming.time && (
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" /> {stats.upcoming.time}
                    </span>
                  )}
                  {stats.upcoming.location_name && (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" /> {stats.upcoming.location_name}
                    </span>
                  )}
                </div>
                {stats.upcoming.description && (
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-2">
                    {stats.upcoming.description}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-md border border-dashed border-slate-200 p-8 text-center">
              <div className="text-sm text-slate-500 mb-3">No events scheduled.</div>
              <Link
                to="/calendar"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-900 underline underline-offset-4"
                data-testid="widget-add-first-event"
              >
                Add your first event <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-6" data-testid="widget-verse-of-day">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-amber-700 mb-1">
            Quran Connections
          </div>
          <div className="font-display text-lg font-semibold text-slate-900">
            Verse sample
          </div>

          {stats.verses_sample?.[0] ? (
            <div className="mt-5">
              <div
                dir="rtl"
                className="font-display text-xl leading-relaxed text-slate-900"
              >
                {stats.verses_sample[0].arabic}
              </div>
              <p className="mt-3 text-sm text-slate-600 italic leading-relaxed">
                {stats.verses_sample[0].translation}
              </p>
              <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                <Link2 className="h-3.5 w-3.5" />
                <span className="font-mono">
                  Surah {stats.verses_sample[0].surah_number} ·{" "}
                  v{stats.verses_sample[0].verse_number}
                </span>
              </div>
            </div>
          ) : (
            <div className="mt-5 text-sm text-slate-500">Loading verses…</div>
          )}

          <Link
            to="/quran"
            className="mt-6 inline-flex items-center gap-1 text-xs font-medium text-slate-900 underline underline-offset-4"
            data-testid="widget-open-quran"
          >
            Open reader <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>

      <section className="mt-10 rounded-md border border-slate-200 bg-white p-6" data-testid="widget-forge-summary">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-indigo-700 mb-1">
              App Forge
            </div>
            <div className="font-display text-lg font-semibold text-slate-900">
              Your forge at a glance
            </div>
          </div>
          <Link
            to="/forge"
            className="text-xs font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid="widget-open-forge"
          >
            Open Forge <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Templates" value={stats.templates} icon={Layers} />
          <Stat label="Your creations" value={stats.forge_creations} icon={Hammer} />
          <Stat label="Events" value={stats.events} icon={CalendarRange} />
          <Stat label="Surahs" value={stats.surahs} icon={BookOpen} />
        </div>
      </section>
    </Layout>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">
          {label}
        </div>
        <Icon className="h-3.5 w-3.5 text-slate-400" />
      </div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-2">
        {value}
      </div>
    </div>
  );
}
